import type { MotionExport } from './motion'

/**
 * `modality.json` — die Kanalbeschreibung für GR00T.
 *
 * WAS SIE BEANTWORTET. Der Datensatz führt je Zeitschritt zwei Zahlenreihen:
 * Zustand und Aktion. Für GR00T sind das erst einmal zwei namenlose Vektoren.
 * `modality.json` sagt, wie sie zerfallen — welcher Abschnitt welches Gelenk
 * ist und was er bedeutet. Ohne sie ist Gen5 für die Aufgabenebene ein Roboter
 * mit siebenundzwanzig unbenannten Zahlen.
 *
 * SIE IST DER ANMELDESCHEIN EINER NEUEN BAUFORM. GR00T kennt fertige
 * Bauformen; Gen5 ist keine davon. Der dafür vorgesehene Weg ist die Marke
 * `new_embodiment` zusammen mit einer eigenen Kanalbeschreibung — und die
 * lässt sich nicht von Hand pflegen, ohne beim ersten verschobenen Gelenk
 * falsch zu werden. Sie wird deshalb erzeugt, wie alles andere in dieser Kette.
 *
 * DIE PASSIVEN ACHSEN STEHEN IM ZUSTAND UND NICHT IN DER AKTION. Der Regler
 * muss wissen, wo die Zehe steht — kommandieren kann er sie nicht. Wer die
 * Feder in den Aktionsraum nimmt, lernt gegen ein Stellglied, das es nicht
 * gibt; die Trennung ist der eigentliche Zweck dieser Datei.
 *
 * KEINE BILDER. Ein Datensatz aus der Simulation hat keine Kameras. Der
 * Abschnitt `video` bleibt deshalb leer und nicht mit einem erfundenen
 * Kameranamen gefüllt — eine Bauform, die eine Kamera anmeldet, die es nicht
 * gibt, scheitert erst beim Laden.
 */

/** Ein Abschnitt einer Zahlenreihe: von wo bis wo, und was er bedeutet. */
export interface ModalitySlice {
  start: number
  end: number
}

export interface ModalityConfig {
  state: Record<string, ModalitySlice>
  action: Record<string, ModalitySlice>
  video: Record<string, never>
  annotation: Record<string, ModalitySlice>
}

/**
 * Baut die Kanalbeschreibung aus dem Bewegungsdatensatz.
 *
 * EIN ABSCHNITT JE ACHSE und nicht ein Abschnitt für alle. Das ist mehr Text,
 * und es ist die einzige Form, in der die Beschreibung etwas nützt: sie erlaubt
 * es, eine einzelne Achse zu benennen, auszunehmen oder anders zu normieren.
 * Ein einziger Abschnitt „joints 0…27" sagt genau so viel wie gar keine Datei.
 */
export function modalityConfig(motion: MotionExport): ModalityConfig {
  const state: Record<string, ModalitySlice> = {}
  const action: Record<string, ModalitySlice> = {}

  motion.joints.forEach((joint, i) => {
    state[joint.name] = { start: i, end: i + 1 }
    /* Die Aktion überspringt die passiven Achsen — siehe Kopfkommentar. Sie
       behält dabei die Nummerierung des ZUSTANDS, damit sich beide Reihen
       Achse für Achse nebeneinanderlegen lassen. */
    if (!joint.passive) action[joint.name] = { start: i, end: i + 1 }
  })

  return {
    state,
    action,
    video: {},
    annotation: { 'annotation.human.task_description': { start: 0, end: 1 } },
  }
}

/**
 * Die Datei, wie GR00T sie liest.
 *
 * Mit der Modellkennung im Kopf: eine Kanalbeschreibung ohne Modellstand
 * beschreibt irgendeinen Gen5, und davon gibt es mit jeder verschobenen Achse
 * einen neuen.
 */
export function modalityJson(motion: MotionExport): string {
  const cfg = modalityConfig(motion)
  return (
    JSON.stringify(
      {
        _comment: `NEURA Performance · ${motion.model} · Modellstand ${motion.modelId} · erzeugt, nicht gepflegt`,
        ...cfg,
      },
      null,
      2,
    ) + '\n'
  )
}
